import {
  EmailOtpRequestPayload,
  EmailOtpRequestResponse,
  EmailOtpVerifyPayload,
  EmailOtpVerifyResponse,
  LoginForm,
  SsoLoginParams,
  SsoLoginResponse,
  ApplyAccessRequest,
  User,
  QuickbooksLoginParams,
  QuickbooksLoginResponse,
  CheckHasLinkToQuickBooksRequest,
  CheckHasLinkToQuickBooksResponse,
  SelfRegisterRequest,
} from 'domains/appDomain';
import { EventSourcePolyfill } from 'event-source-polyfill';
import jwt_decode from 'jwt-decode';
import api from '../utils/api';

/** Response shape when gateway returns OAuth redirect (SPA flow). */
export interface OAuthRedirectResponse {
  oauthRedirect: true;
  redirectUrl: string;
  /** Same JWT as non-OAuth login; persist before redirect so RevShop tabs stay signed in. */
  token?: string;
  createdUser?: boolean;
}

/** Sentinel returned from login() when OAuth redirect is in progress; caller should skip pathname logic. */
export const OAUTH_REDIRECT_SENTINEL: unique symbol = Symbol('OAUTH_REDIRECT');

function isOAuthRedirectResponse(data: unknown): data is OAuthRedirectResponse {
  return (
    data != null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    (data as OAuthRedirectResponse).oauthRedirect === true &&
    typeof (data as OAuthRedirectResponse).redirectUrl === 'string' &&
    (data as OAuthRedirectResponse).redirectUrl.length > 0
  );
}

export function persistAuthTokenForBrowserSession(token: string) {
  localStorage.setItem('Authorization', token);
  try {
    const user = jwt_decode<User>(token);
    if (user?.orgId != null) {
      localStorage.setItem('org', String(user.orgId));
    }
  } catch {
    // ignore decode errors
  }
}

const appService = {
  login: async (
    loginForm: LoginForm
  ): Promise<User | typeof OAUTH_REDIRECT_SENTINEL> => {
    const data = await api.post<string | OAuthRedirectResponse>(
      '/api/login',
      loginForm,
      {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    );
    if (isOAuthRedirectResponse(data)) {
      if (data.token) {
        persistAuthTokenForBrowserSession(data.token);
      }
      window.location.href = data.redirectUrl;
      return OAUTH_REDIRECT_SENTINEL;
    }
    const jwtToken =
      typeof data === 'string' ? data : (data as unknown as string);
    localStorage.setItem('Authorization', jwtToken);
    return jwt_decode<User>(jwtToken);
  },

  requestEmailOtp: async (
    payload: EmailOtpRequestPayload
  ): Promise<EmailOtpRequestResponse> => {
    return await api.post<EmailOtpRequestResponse>(
      '/api/email-otp/request',
      payload,
      {
        'Content-Type': 'application/json',
      }
    );
  },

  verifyEmailOtp: async (
    payload: EmailOtpVerifyPayload
  ): Promise<
    { user: User; createdUser: boolean } | typeof OAUTH_REDIRECT_SENTINEL
  > => {
    const response = await api.post<
      EmailOtpVerifyResponse | OAuthRedirectResponse
    >('/api/email-otp/verify', payload, {
      'Content-Type': 'application/json',
    });
    if (isOAuthRedirectResponse(response)) {
      if (response.token) {
        persistAuthTokenForBrowserSession(response.token);
      }
      window.location.href = response.redirectUrl;
      return OAUTH_REDIRECT_SENTINEL;
    }
    persistAuthTokenForBrowserSession(response.token);
    return {
      user: jwt_decode<User>(response.token),
      createdUser: response.createdUser,
    };
  },

  /**
   * One-time exchange after Canva SSO consume redirect (query {@code canvaExchange}).
   * Gateway: {@code POST /sso/canva/exchange}.
   */
  exchangeCanvaSso: async (code: string): Promise<{ token: string }> => {
    return await api.post<{ token: string }>(
      '/api/sso/canva/exchange',
      { code },
      {
        'Content-Type': 'application/json',
      },
      { useGlobalErrorHandler: false }
    );
  },

  ssoInit: async () => {
    const ssoUrl = await api.get<string>('/api/sso/initialize');
    return ssoUrl;
  },

  ssoLogin: async (ssoParams: SsoLoginParams) => {
    const response = await api.post<SsoLoginResponse>(
      '/api/sso-login',
      ssoParams,
      {
        'Content-Type': 'application/json',
      }
    );
    console.log('SSO Login response : ', response);
    if (response.token) {
      localStorage.setItem('Authorization', response.token);
      return {
        user: jwt_decode<User>(response.token),
        response,
      };
    }

    return {
      user: null,
      response,
    };
  },

  selfRegister: async (request: SelfRegisterRequest) => {
    const response = await api.post<SsoLoginResponse>(
      '/api/self-register',
      request,
      {
        'Content-Type': 'application/json',
      }
    );

    if (response.token) {
      localStorage.setItem('Authorization', response.token);
      return {
        user: jwt_decode<User>(response.token),
        response,
      };
    }

    return {
      user: null,
      response,
    };
  },

  quickbooksLogin: async (quickbooksLoginParams: QuickbooksLoginParams) => {
    const response = await api.post<QuickbooksLoginResponse>(
      '/payments/quickbooks/login',
      quickbooksLoginParams,
      {
        'Content-Type': 'application/json',
      }
    );

    return response;
  },

  checkHasLinkedToQuickBooks: async (
    request: CheckHasLinkToQuickBooksRequest
  ) => {
    const response = await api.post<CheckHasLinkToQuickBooksResponse>(
      '/payments/quickbooks/checkHasLinked',
      request,
      {
        'Content-Type': 'application/json',
      }
    );

    return response;
  },

  applyAccess: async (request: ApplyAccessRequest) => {
    return await api.post('/api/sso/apply-access', request, {
      'Content-Type': 'application/json',
    });
  },

  getUserFromJwtToken: () => {
    const authToken = localStorage.getItem('Authorization');
    if (!authToken) return null;
    try {
      const user = jwt_decode<User>(authToken);

      if (Date.now() > user.exp * 1000) {
        // jwt is expired
        localStorage.removeItem('Authorization');
        return null;
      } else {
        return user;
      }
    } catch (e) {
      // token format is invalid
      localStorage.removeItem('Authorization');
      return null;
    }
  },
  heartbeatCheck: async () => {
    return await fetch(`${process.env.REACT_APP_API_URL}/ping`);
  },
  eventSource: () => {
    return api.eventsource<EventSourcePolyfill>(`/events`);
  },
};
export default appService;
